/**
 * Nails Studio Prompt Builder
 * 
 * Builds prompts for manicure and pedicure simulations
 * Mode is auto-detected via MediaPipe (hands vs feet)
 */

/**
 * Build the complete AI prompt for nails generation
 * 
 * @param {Object} params - Parameters for prompt building
 * @param {string} params.mode - 'manicure' or 'pedicure' (auto-detected)
 * @param {string} params.shape - Nail shape (square, round, almond, etc.)
 * @param {string} params.length - Nail length (short, medium, long) - only for manicure
 * @param {string} params.color - Nail color
 * @param {string} params.finish - Nail finish (glossy, matte, chrome, etc.)
 * @param {string} params.customPrompt - User's custom text input (optional)
 * @returns {string} - Complete AI prompt
 */
export const buildNailsPrompt = ({ mode, shape, length, color, finish, customPrompt }) => {
  const isManicure = mode === 'manicure';
  const isPedicure = mode === 'pedicure';

  let prompt = '';

  // ===== MANICURE PROMPT =====
  if (isManicure) {
    prompt = 'Edit ONLY the fingernails in the image.\n\n';
    prompt += 'Apply a professional manicure with:\n';
    
    if (shape) {
      prompt += `- Nail shape: ${shape}\n`;
    }
    if (length) {
      prompt += `- Nail length: ${length}\n`;
    }
    if (color) {
      prompt += `- Nail color: ${color}\n`;
    }
    if (finish) {
      prompt += `- Nail finish: ${finish}\n`;
    }

    prompt += '\nSTRICT RULES:\n';
    prompt += '- Modify fingernails ONLY\n';
    prompt += '- Do NOT change skin, fingers, hands, face, or background\n';
    prompt += '- Do NOT reshape hands or fingers\n';
    prompt += '- Do NOT blur or smooth skin\n';
    prompt += '- Preserve lighting and shadows exactly\n';
  }

  // ===== PEDICURE PROMPT =====
  if (isPedicure) {
    prompt = 'Edit ONLY the toenails in the image.\n\n';
    prompt += 'Apply a professional pedicure with:\n';
    
    if (shape) {
      prompt += `- Nail shape: ${shape}\n`;
    }
    // Note: No length for pedicure (toenails don't vary as much)
    if (color) {
      prompt += `- Nail color: ${color}\n`;
    }
    if (finish) {
      prompt += `- Nail finish: ${finish}\n`;
    }

    prompt += '\nSTRICT RULES:\n';
    prompt += '- Modify toenails ONLY\n';
    prompt += '- Do NOT change feet, toes, skin, or background\n';
    prompt += '- Do NOT apply beauty filters or smoothing\n';
    prompt += '- Preserve lighting and shadows exactly\n';
  }

  // ===== USER CUSTOM REQUEST =====
  if (customPrompt && customPrompt.trim()) {
    prompt += `\nUser additional request:\n${customPrompt.trim()}\n`;
  }

  // ===== QUALITY CONSTRAINTS =====
  prompt += '\nPhotorealistic, natural appearance.\n';
  prompt += 'Return only the final edited image.';

  return prompt;
};

/**
 * Validate prompt parameters
 * @param {Object} params - Parameters to validate
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export const validateNailsPromptParams = (params) => {
  const errors = [];

  if (!params.mode) {
    errors.push('Mode (manicure/pedicure) is required');
  }

  if (!params.shape) {
    errors.push('Nail shape is required');
  }

  if (!params.color) {
    errors.push('Nail color is required');
  }

  if (!params.finish) {
    errors.push('Nail finish is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export default buildNailsPrompt;
